import {
  Breakpoints,
  CTA,
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
  styled,
} from '@volkswagen-onehub/components-core';
import { useEffect, useMemo} from 'react';
import { useTribalContext } from '@/context/tribal';
import Box from '../components/Box/Box';
import useGlobalStore from '@/stores/globalStore';
import {
  getNewCarsDepartment,
  getRetailerAddress,
  getRetailerOpeningTimes,
  isAllNotBuildable,
} from '../utils/storeUtils';
import FooterRetailerTimes from '../components/Footer/FooterRetailer/FooterRetailerTimes';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import usePdb from '@/hooks/usePdb';
import { ButtonWrapper } from '@/styles';
import {
  performVWBasicButtonClick,
  performVWBasicDownloadLinkClick,
  performVWBasicErrorMessageLoad,
  performVWBasicPageLoad,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { PageContainer } from '../components';

const Header = styled.div(
  ({ theme }) => `
  background-blend-mode: multiply;
  background: linear-gradient(0deg, #F0EEE9 -0.9%, rgba(240, 238, 233, 0) 82.93%);


  padding: ${theme.size.static500} 0;

  @media (min-width: ${Breakpoints.b1280}px) {
    padding: ${theme.size.static1200} 0;

    strong {
      display: block;
    }
  }
`
);

const Container = styled.div(
  ({ theme }) => `
  text-align:center;
  margin-bottom: ${theme.size.static500};

  @media (min-width: ${Breakpoints.b960}px) {
    margin-bottom: ${theme.size.static575};
  }
`
);

const RetailerInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Expired = () => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const { configurations, retailers, pdbId } = useGlobalStore(
    (state) => state.app
  );
  const { pdfUrl } = usePdb({ pdbId });

  const downloadPdfLabel = contentManager.get('ctas-download-pdf');
  const carConfiguratorUrl = contentManager.get('car-configurator-href');
  const carConfiguratorLabel = contentManager.get('ctas-goto-configurator');

  const newCarsDepartment = getNewCarsDepartment(
    retailers[0]?.departments || []
  );

  const retailerAddress = getRetailerAddress([
    newCarsDepartment?.street,
    newCarsDepartment?.district || '',
    newCarsDepartment?.town || '',
    newCarsDepartment?.postcode,
  ]);

  const analyticsCommonFields = {
    configurations,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  performVWBasicPageLoad(analyticsCommonFields);

  useEffect(() => {
    performVWBasicErrorMessageLoad({
      ...analyticsCommonFields,
      error: {
        message: contentManager.get('expired-screen-title-bold'),
      },
    });
  }, []);

  return (
    <>
      <Container>
        <Header>
          <PageContainer>
            <Box mb={DesignTokenSizeEnum.dynamic0100}>
              <SectionHeader
                title=""
                titleBold={`${contentManager.get('expired-screen-title-bold')} ${contentManager.get('expired-screen-title-normal')}`}
                titleSize={TokenTextAppearance.headline500}
                type="boldLight"
              />
            </Box>
            <Box
              as={Text}
              appearance={TokenTextAppearance.headline200}
              tag={TextTag.h3}
              mb={DesignTokenSizeEnum.dynamic0100}
            >
              {contentManager.get('expired-screen-apology')}
            </Box>
            
            { isAllNotBuildable(configurations) && (
              <><Box
                as={Text}
                appearance={TokenTextAppearance.headline200}
                tag={TextTag.h3}
                mb={DesignTokenSizeEnum.dynamic0150}
              >
                {contentManager.get('expired-screen-brochure-download')}
              </Box><ButtonWrapper>
                  <CTA
                    emphasis="primary"
                    tag="a"
                    size="large"
                    href={pdfUrl}
                    onClick={() => performVWBasicDownloadLinkClick({
                      label: downloadPdfLabel,
                      sectionName: SectionNames.EXPIRED_SCREEN,
                      configurations,
                      retailers,
                      analyticsManager,
                      analyticsBuilder,
                    })}
                  >
                    {downloadPdfLabel}
                  </CTA>
                </ButtonWrapper></>
            )}  
          </PageContainer>
        </Header>
      </Container>

      <PageContainer>
        <Container>
          <Box mb={DesignTokenSizeEnum.dynamic0100}>
            <SectionHeader
              title=""
              titleSize={TokenTextAppearance.headline400}
              titleBold={`${contentManager.get('configure-another-model-title-normal')} ${contentManager.get('configure-another-model-title-bold')}`}
            />
          </Box>
          <Box
            as={Text}
            appearance={TokenTextAppearance.headline200}
            tag={TextTag.h3}
            mb={DesignTokenSizeEnum.dynamic0150}
          >
            {contentManager.get('configure-another-model-subtitle')}
          </Box>
          <ButtonWrapper>
            <CTA
              emphasis="secondary"
              tag="a"
              href={carConfiguratorUrl}
              size="large"
              onClick={() =>
                performVWBasicButtonClick({
                  label: carConfiguratorLabel,
                  sectionName: SectionNames.EXPIRED_SCREEN,
                  configurations,
                  retailers,
                  analyticsManager,
                  analyticsBuilder,
                })
              }
            >
              {carConfiguratorLabel}
            </CTA>
          </ButtonWrapper>
        </Container>

        <Container>
          <Box mb={DesignTokenSizeEnum.dynamic0100}>
            <SectionHeader
              title=''
              titleBold={`${contentManager.get('your-retailer-title-normal')} ${contentManager.get('your-retailer-title-bold')}`}
              titleSize={TokenTextAppearance.headline400}
            />
          </Box>
          <Box
            as={Text}
            appearance={TokenTextAppearance.headline200}
            tag={TextTag.h3}
            mb={DesignTokenSizeEnum.dynamic0150}
          >
            {contentManager.get('your-retailer-subtitle')}
          </Box>
          <RetailerInfo>
            <Box
              as={Text}
              appearance={TokenTextAppearance.headline300}
              tag={TextTag.h2}
              mb={DesignTokenSizeEnum.static350}
            >
              <strong>{newCarsDepartment?.tradingName}</strong>
            </Box>
            <Box
              as={Text}
              tag={TextTag.p}
              appearance={TokenTextAppearance.copy100}
              mb={DesignTokenSizeEnum.static250}
            >
              {retailerAddress}
            </Box>
            {newCarsDepartment?.openingHours && (
              <FooterRetailerTimes
                openingTimes={getRetailerOpeningTimes(
                  newCarsDepartment?.openingHours
                )}
                emailRetailerCta={false}
                contactEmail={newCarsDepartment?.contactEmail}
                retailerAddress={retailerAddress}
                retailerNumber={newCarsDepartment?.telephone}
                retailerNumberSize={TokenTextAppearance.copy200}
                contactRetailerCta
                centerRetailerLinks
                sectionName={SectionNames.EXPIRED_SCREEN}
              />
            )}
          </RetailerInfo>
        </Container>
      </PageContainer>
    </>
  );
};

export default Expired;
